import { BRAND_THEME_CONFIG_VERSION, type BrandSettings, type BrandThemeConfig } from '@digital-twins/shared-types'
import { DEFAULT_CHART_PALETTE_ID, getChartPalette } from '@digital-twins/deck-ui/foundations/theme/chart-palettes'
import {
  FAIRGEN_PRIMARY_HSL,
  FAIRGEN_SOFT_ACCENT_DARK_HSL,
  FAIRGEN_SOFT_ACCENT_HSL,
} from '@digital-twins/deck-ui/foundations/theme/deck-design-system'

/** Fairgen mint reference (#81D7BF) — not used for CTA/buttons; decorative only. */
export const FAIRGEN_TWINS_MINT_HSL = '163 52% 67%' as const

/** @deprecated Brief #81D7BF experiment — persisted in some org brandSettings; treat as platform default. */
export const LEGACY_MISTAKEN_MINT_HSL = FAIRGEN_TWINS_MINT_HSL

// The colour values are deck-ui's (one owner): the brand primary seeds the deck
// design system, the soft accent and chart palette are the brand apply's defaults.
/** HSL tuple for Fairgen accent teal (#0EBFA5). */
export const FAIRGEN_TWINS_PRIMARY_HSL = FAIRGEN_PRIMARY_HSL

/** HSL tuple for Fairgen card/mint accent. */
export const FAIRGEN_TWINS_SECONDARY_HSL = FAIRGEN_SOFT_ACCENT_HSL
export const FAIRGEN_TWINS_CARD_DARK_HSL = FAIRGEN_SOFT_ACCENT_DARK_HSL
export const FAIRGEN_TWINS_DEEP_LIGHT_HSL = '177 53% 8%' as const
export const FAIRGEN_TWINS_DEEP_DARK_HSL = '167 37% 10%' as const
export const FAIRGEN_TWINS_CTA_LIGHT_HSL = '177 53% 8%' as const
export const FAIRGEN_TWINS_CTA_DARK_HSL = '158 54% 77%' as const
export const FAIRGEN_TWINS_DESTRUCTIVE_LIGHT_HSL = '0 72% 45%' as const
export const FAIRGEN_TWINS_DESTRUCTIVE_DARK_HSL = '0 80% 62%' as const
export const FAIRGEN_TWINS_CHART_PALETTE_ID = DEFAULT_CHART_PALETTE_ID
const defaultChartPalette = getChartPalette(DEFAULT_CHART_PALETTE_ID)
export const FAIRGEN_TWINS_CHART_ACCENT_HSL = defaultChartPalette.colors[defaultChartPalette.primaryIndex]

/**
 * Public asset paths for the platform-default Fairgen Twins identity.
 * These match the fallbacks used by Sidebar, SpaAppShellLayout, and index.html
 * before any org-specific brand is configured.
 */
export const FAIRGEN_TWINS_LOGO_URL = '/fairgen-logo-light.svg'
export const FAIRGEN_TWINS_LOGO_URL_ON_DARK = '/fairgen-logo-dark.svg'

/** @deprecated Pre-refresh "Fairgen Twins" lockups — still treated as platform-default in stored org settings. */
// The legacy files in public/ now carry the new Fairgen lockup content, so raw stored paths render correctly.
const LEGACY_FAIRGEN_TWINS_LOGO_URLS = [
  '/fairgen-twins-logo-horizontal-light.svg',
  '/fairgen-twins-logo-horizontal.svg',
] as const
export const FAIRGEN_TWINS_ICON_LOGO_URL = '/icon.svg'

/** Maps stored pre-refresh "Fairgen Twins" lockup paths to the current Fairgen lockup files. */
export function resolveLegacyFairgenLogoUrl(v: string | null | undefined): string | null {
  if (v === '/fairgen-twins-logo-horizontal-light.svg') {
    return FAIRGEN_TWINS_LOGO_URL
  }
  if (v === '/fairgen-twins-logo-horizontal.svg') {
    return FAIRGEN_TWINS_LOGO_URL_ON_DARK
  }
  return v ?? null
}

export function getFairgenTwinsDefaultBrandThemeConfig(): BrandThemeConfig {
  return {
    version: BRAND_THEME_CONFIG_VERSION,
    seeds: {
      accent: {
        light: FAIRGEN_TWINS_PRIMARY_HSL,
        dark: FAIRGEN_TWINS_PRIMARY_HSL,
      },
      card: {
        light: FAIRGEN_TWINS_SECONDARY_HSL,
        dark: FAIRGEN_TWINS_CARD_DARK_HSL,
      },
      deep: {
        light: FAIRGEN_TWINS_DEEP_LIGHT_HSL,
        dark: FAIRGEN_TWINS_DEEP_DARK_HSL,
      },
      cta: {
        light: FAIRGEN_TWINS_CTA_LIGHT_HSL,
        dark: FAIRGEN_TWINS_CTA_DARK_HSL,
      },
      destructive: {
        light: FAIRGEN_TWINS_DESTRUCTIVE_LIGHT_HSL,
        dark: FAIRGEN_TWINS_DESTRUCTIVE_DARK_HSL,
      },
      chartAccent: {
        light: FAIRGEN_TWINS_CHART_ACCENT_HSL,
        dark: FAIRGEN_TWINS_CHART_ACCENT_HSL,
      },
    },
    chartPaletteId: FAIRGEN_TWINS_CHART_PALETTE_ID,
  }
}

/** Canonical stored brand payload applied by "Reset brand" and the Fairgen preset. */
export function getFairgenTwinsDefaultBrandSettings(): BrandSettings {
  return {
    brandPrimaryColor: FAIRGEN_TWINS_PRIMARY_HSL,
    brandSecondaryColor: FAIRGEN_TWINS_SECONDARY_HSL,
    brandThemeConfig: getFairgenTwinsDefaultBrandThemeConfig(),
    brandLogoUrl: FAIRGEN_TWINS_LOGO_URL,
    brandLogoUrlOnDark: FAIRGEN_TWINS_LOGO_URL_ON_DARK,
    brandIconLogoUrl: FAIRGEN_TWINS_ICON_LOGO_URL,
    brandDomain: null,
    brandRoleGraph: null,
    brandRoleGraphVersion: null,
    brandRoleGraphHash: null,
    themePresetId: null,
    brandFontFamily: null,
    brandHeadingFontFamily: null,
    themeOverrides: null,
    shareFooterText: null,
  }
}

/**
 * True when stored settings are effectively the Fairgen Twins platform default —
 * meaning there is nothing custom left to reset.
 *
 * Null values for colors and logos are treated as equivalent to the Fairgen defaults
 * because `applyBrandColors` uses identical CSS-variable fallbacks for both null and
 * the explicit Fairgen HSL/path values. A fresh user whose DB fields are all-null
 * (e.g. skipped the onboarding brand step) is already on the Fairgen experience and
 * should see the reset button as disabled.
 */
export function isFairgenTwinsDefaultBrand(settings: BrandSettings): boolean {
  const overridesEmpty =
    !settings.themeOverrides || Object.keys(settings.themeOverrides).length === 0

  const colorIsDefault = (v: string | null | undefined) =>
    !v || v === FAIRGEN_TWINS_PRIMARY_HSL

  const secondaryIsDefault = (v: string | null | undefined) =>
    !v ||
    v === FAIRGEN_TWINS_SECONDARY_HSL ||
    v === LEGACY_MISTAKEN_MINT_HSL

  const logoIsDefault = (v: string | null | undefined, canonical: string) =>
    !v || v === canonical || (LEGACY_FAIRGEN_TWINS_LOGO_URLS as ReadonlyArray<string>).includes(v)

  // No org ships a default `ambientField` override (the Fairgen homepage stays
  // pinned to its literal hue table, never derived — see
  // docs/plans/brand-derived-ambient-gradient.md D1). An org that sets only an
  // `ambientField` override with everything else untouched must NOT read as
  // "Fairgen default", or the D2 branded gate would silently discard it.
  const ambientFieldIsDefault = (value: BrandThemeConfig['ambientField'] | undefined) => {
    if (value === undefined || value === null) {
      return true
    }
    return (value.light ?? null) === null && (value.dark ?? null) === null && (value.gradients ?? null) === null
  }

  function themeConfigIsDefault(config: BrandThemeConfig | null | undefined) {
    if (!config) return true

    const defaultConfig = getFairgenTwinsDefaultBrandThemeConfig()
    if (config.version !== defaultConfig.version) return false
    if ((config.chartPaletteId ?? null) !== defaultConfig.chartPaletteId) return false
    if (!ambientFieldIsDefault(config.ambientField)) return false

    const seedKeys = ['accent', 'card', 'deep', 'cta', 'destructive', 'chartAccent'] as const
    const legacySeedValues: Partial<
      Record<(typeof seedKeys)[number], { light?: Array<string>; dark?: Array<string> }>
    > = {
      card: { light: [LEGACY_MISTAKEN_MINT_HSL], dark: ['177 53% 8%'] },
      deep: { dark: ['171 52% 8%', '177 53% 8%'] },
      cta: { light: [LEGACY_MISTAKEN_MINT_HSL], dark: [LEGACY_MISTAKEN_MINT_HSL, '154 85% 84%', '158 52% 77%'] },
    }

    const seedValueMatches = (
      seedKey: (typeof seedKeys)[number],
      mode: 'light' | 'dark',
      value: string | null | undefined,
      canonical: string | null | undefined,
    ) => {
      if (value === canonical) {
        return true
      }
      const legacy = legacySeedValues[seedKey]?.[mode] ?? []
      return legacy.includes(value ?? '')
    }

    return seedKeys.every((seedKey) => {
      const seed = config.seeds?.[seedKey]
      const defaultSeed = defaultConfig.seeds?.[seedKey]
      return (
        seedValueMatches(seedKey, 'light', seed?.light, defaultSeed?.light) &&
        seedValueMatches(seedKey, 'dark', seed?.dark, defaultSeed?.dark)
      )
    })
  }

  return (
    colorIsDefault(settings.brandPrimaryColor) &&
    secondaryIsDefault(settings.brandSecondaryColor) &&
    themeConfigIsDefault(settings.brandThemeConfig) &&
    logoIsDefault(settings.brandLogoUrl, FAIRGEN_TWINS_LOGO_URL) &&
    logoIsDefault(settings.brandLogoUrlOnDark, FAIRGEN_TWINS_LOGO_URL_ON_DARK) &&
    logoIsDefault(settings.brandIconLogoUrl, FAIRGEN_TWINS_ICON_LOGO_URL) &&
    !settings.brandDomain &&
    settings.themePresetId == null &&
    settings.brandFontFamily == null &&
    settings.brandHeadingFontFamily == null &&
    overridesEmpty &&
    !settings.shareFooterText
  )
}
